/**
 * Elevation tokens.
 *
 * Depth is communicated by tone and edge. Shadow marks only elements that
 * float over the page. One rung per overlay class:
 *
 *   0  in-page, flat. Hairline border, no shadow.
 *   1  in-page, raised. Hairline border and a 1px contact shadow. Use sparingly.
 *   2  popover, menu.
 *   3  dialog.
 *   4  sheet, toast.
 */

import { color } from "./color";

const SHADOW = color.effect.shadow;

const mix = (opacity: number): string =>
    `color-mix(in oklch, ${SHADOW} ${(opacity * 100).toFixed(1)}%, transparent)`;

export type ElevationLevel = 0 | 1 | 2 | 3 | 4;

export interface ElevationBundle {
    /** `box-shadow` value. */
    readonly shadow: string;
    /** Border color for the hairline edge. */
    readonly border: string;
}

const popover: ElevationBundle = {
    shadow: [`0 1px 2px ${mix(0.24)}`, `0 8px 24px ${mix(0.18)}`].join(", "),
    border: color.border.subtle,
};

const dialog: ElevationBundle = {
    shadow: [`0 2px 4px ${mix(0.24)}`, `0 16px 48px ${mix(0.28)}`].join(", "),
    border: color.border.subtle,
};

const sheet: ElevationBundle = {
    shadow: [`0 -1px 0 ${mix(0.12)}`, `0 12px 40px ${mix(0.32)}`].join(", "),
    border: color.border.subtle,
};

export const elevation: Record<ElevationLevel, ElevationBundle> = {
    0: {
        shadow: "none",
        border: color.border.subtle,
    },
    1: {
        shadow: `0 1px 2px ${mix(0.1)}`,
        border: color.border.subtle,
    },
    2: popover,
    3: dialog,
    4: sheet,
};

/**
 * Opaque tonal surface for sticky and floating chrome (top bar, scene and
 * section headers, action dock): one tonal step above the canvas, so the
 * step alone separates it. No backdrop blur anywhere: a blurred surface is a
 * render pass on every frame that anything beneath it moves, and it kept the
 * GPU awake while the page was idle.
 */
export const chromeSurface = {
    background: color.bg.surface,
} as const;
